import {useCallback, useState} from 'react';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import {useTranslation} from "react-i18next";
import {ArrowBackIos} from "@mui/icons-material";
import Button from "@mui/material/Button";
import {RoutePaths} from "../../util/constants";
import DrawerList from "../../component/shared/drawer-list/DrawerList";
import usePanelDrawerItems from "./usePanelDrawerItems";
import ScanWinnerDialog from "../../component/dialogs/scan-winner-dialog/ScanWinnerDialog";
import ProfileDropdown from "../../component/shared/profile-dropdown/ProfileDropdown";
import {alpha, Skeleton} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {RootState, useAppSelector} from "../../store";
import ValidateVoucherDialog from "../../component/dialogs/validate-voucher-dialog/ValidateVoucherDialog";
import {getRandomNumber} from "../../util/utils";
import {useNavigate} from "react-router-dom";
import backgroundImage from "../../assets/images/background.jpg";

const drawerWidth = 260;

interface Props {
    /**
     * Injected by the documentation to work in an iframe.
     * You won't need it on your project.
     */
    window?: () => Window;
    children: React.ReactNode
}

const PanelLayout = (props: Props) => {
    const {window, children} = props;
    const isAdmin = useAppSelector((state: RootState) => state.authReducer.loggedInUser.is_admin)
    const isClient = useAppSelector((state: RootState) => !!state.authReducer.loggedInUser.client_id)
    const clientName = useAppSelector((state: RootState) => state.clientReducer.selectedClient.name)
    const clientLogo = useAppSelector((state: RootState) => state.clientReducer.selectedClient.company_logo)
    const fetchClientLoading = useAppSelector((state: RootState) => state.clientReducer.fetchClientLoading)
    const [mobileOpen, setMobileOpen] = useState(false);
    const {t} = useTranslation()
    const navigate = useNavigate()
    const {adminDrawerListItems, clientDrawerListItems} = usePanelDrawerItems()
    const [openScanWinnerDialog, setOpenScanWinnerDialog] = useState(false)
    const [openValidateVoucherDialog, setOpenValidateVoucherDialog] = useState(false)
    const theme = useTheme()
    const panelBackground = `url(${backgroundImage})`
    const panelBackgroundOverlay = `linear-gradient(${alpha(theme.palette.primary.main, 0.9)}, ${alpha(theme.palette.primary.main, 0.9)}), ${panelBackground}`

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const container = window !== undefined ? () => window().document.body : undefined;

    const handleHomeButtonClick = () => {
        navigate(RoutePaths.HOME)
    }

    const handleDrawerButtonClick = useCallback(() => {
        if (isAdmin) {
            setOpenScanWinnerDialog(true)
            return
        }
        if (isClient) {
            setOpenValidateVoucherDialog(true)
        }
    }, [isAdmin, isClient])

    return (
        <Box sx={{display: 'flex'}}>
            <AppBar
                className="admin-toolbar"
                position="fixed"
                color="secondary"
                sx={{
                    width: {sm: `calc(100% - ${drawerWidth}px)`},
                    ml: {sm: `${drawerWidth}px`},
                    backgroundColor: theme.palette.secondary.main,
                    backgroundImage: panelBackground,
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                }}
            >
                <Toolbar>
                    <IconButton
                        color="primary"
                        aria-label="open drawer"
                        edge="start"
                        onClick={handleDrawerToggle}
                        sx={{mr: 2, display: {sm: 'none'}}}
                    >
                        <MenuIcon/>
                    </IconButton>
                    <Typography variant="h6" fontWeight={600} noWrap component="div"
                                sx={{flexGrow: 1, gap: 1.5, display: 'flex', alignItems: 'center'}}>
                        {fetchClientLoading ? <Skeleton
                            width={`${getRandomNumber(150, 200)}px`}/> : isAdmin ? t('admin_panel') : (<>
                            <img src={clientLogo} alt={clientName} height={50} width="auto"/>
                            {clientName}
                        </> || t('client_panel'))}
                    </Typography>
                    <ProfileDropdown/>
                </Toolbar>
            </AppBar>
            <Box
                component="nav"
                sx={{width: {sm: drawerWidth}, flexShrink: {sm: 0}}}
                aria-label="mailbox folders"
            >
                {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
                <Drawer
                    container={container}
                    variant="temporary"
                    open={mobileOpen}
                    onClose={handleDrawerToggle}
                    ModalProps={{
                        keepMounted: true, // Better open performance on mobile.
                    }}
                    slotProps={{
                        backdrop: {
                            sx: {
                                backgroundColor: alpha(theme.palette.secondary.main, .6),
                                backdropFilter: 'blur(2px)'
                            },
                        },
                    }}
                    sx={{
                        display: {xs: 'block', sm: 'none'},
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            backgroundColor: theme.palette.secondary.main,
                            backgroundImage: panelBackground,
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "cover",
                        },
                    }}
                >
                    <DrawerList drawerListItems={isAdmin ? adminDrawerListItems : clientDrawerListItems}
                                handleButtonClick={handleDrawerButtonClick}
                                toggleDrawer={handleDrawerToggle}/>
                </Drawer>
                <Drawer
                    variant="permanent"
                    sx={{
                        display: {xs: 'none', sm: 'block'},
                        '& .MuiDrawer-paper': {
                            boxSizing: 'border-box',
                            width: drawerWidth,
                            zIndex: theme.zIndex.drawer - 2,
                            backgroundColor: theme.palette.secondary.main,
                            backgroundImage: panelBackground,
                            backgroundPosition: "center",
                            backgroundRepeat: "no-repeat",
                            backgroundSize: "cover",
                        },
                    }}
                    open
                >
                    <DrawerList drawerListItems={isAdmin ? adminDrawerListItems : clientDrawerListItems}
                                handleButtonClick={handleDrawerButtonClick}/>
                </Drawer>
            </Box>
            <Box
                component="main"
                sx={{
                    backgroundColor: alpha(theme.palette.primary.main, .9),
                    backgroundImage: panelBackgroundOverlay,
                    backgroundPosition: "center",
                    backgroundRepeat: "no-repeat",
                    backgroundSize: "cover",
                    minHeight: "100vh",
                    flexGrow: 1,
                    p: 3,
                    width: {xs: '100%', sm: `calc(100% - ${drawerWidth}px)`}
                }}
            >
                <Toolbar/>
                <Button color="secondary"
                        onClick={handleHomeButtonClick}
                        startIcon={<ArrowBackIos/>}>
                    {t('home')}
                </Button>
                <Box mt={2}>
                    {children}
                    {isAdmin && <ScanWinnerDialog open={openScanWinnerDialog}
                                                  handleDialogClose={() => setOpenScanWinnerDialog(false)}/>}
                    {isClient && <ValidateVoucherDialog open={openValidateVoucherDialog}
                                                        handleDialogClose={() => setOpenValidateVoucherDialog(false)}/>}
                </Box>
            </Box>
        </Box>
    );
}
export default PanelLayout
